<template>
  <section class="container-fluid">
    <div class="container mt-5 px-3">
      <!-- Header Section -->
      <div class="row align-items-center mb-5">
        <div class="col-md-6 mb-3 mb-md-0">
          <div class="d-flex align-items-center gap-3">
            <h2 class="mb-1 d-flex align-items-center text-light fs-1">
              Lista de Pedidos<span class="text-primary">.</span>
            </h2>
            <button 
              class="btn btn-outline-primary"
              @click="forceRefresh"
              :disabled="isLoading"
            >
              <i class="fas fa-sync-alt me-2"></i>
              {{ isLoading ? 'Actualizando...' : 'Actualizar' }}
            </button>
          </div>
          <p class="text-muted mb-0 fs-5">
            <i class="fas fa-box-open me-1 text-primary"></i>
            {{ totalOrders }} pedidos registrados
            <span class="text-muted fs-7" data-bs-toggle="tooltip" data-bs-placement="top" title="*Recorda que los pedidos pueden tardar hasta 5 minutos en actualizarse.">
            <i class="fas fa-info-circle"></i>
            </span>
            </p>
          <!-- Filters Section -->
          <div class="col-md-10 my-3 mb-md-0">
            <div class="input-group">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fas fa-search"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0 ps-0 text-black"
                v-model="searchQuery"
                placeholder="Buscar por nombre del cliente..."
                @input="debounceSearch"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="row g-4">
        <div v-for="n in 8" :key="n" class="col-lg-4">
          <div class="skeleton-loader">
            <div class="skeleton-text w-50"></div>
            <div class="skeleton-text w-75"></div>
            <div class="skeleton-text w-75"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
        <button @click="fetchOrders" class="btn btn-outline-danger btn-sm ms-3">
          <i class="fas fa-sync-alt me-1"></i>Reintentar
        </button>
      </div>

      <!-- Orders List -->
      <div v-else class="row g-4">
        <div v-for="order in filteredOrders" :key="order.id" class="col-md-6 col-lg-4">
          <div class="card bg-dark text-light">
            <div class="card-body d-flex flex-column">
              <!-- Order Header -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <span class="order-id text-muted me-2">#{{ order.id }}</span>
                  <span 
                    class="badge"
                    :class="{
                      'bg-warning': order.status === 'pending',
                      'bg-success': order.status === 'completed',
                      'bg-danger': order.status === 'cancelled'
                    }"
                  >
                    <i :class="getStatusIcon(order.status)" class="me-1"></i>
                    {{ getStatusLabel(order.status) }}
                  </span>
                </div>
                <small class="text-muted">{{ formatDate(order.created_at) }}</small>
              </div>

              <!-- Order Info -->
              <div class="order-info mb-3">
                <div class="info-row">
                  <i class="fas fa-user text-primary"></i>
                  <span class="fw-lightbold">
                    {{ order.customer_details?.username || order.recipient_name || 'Sin nombre' }}
                  </span>
                  <div class="ms-auto contact-buttons">
                    <a 
                      v-if="order.customer_phone"
                      :href="getWhatsAppLink(order.customer_phone)"
                      target="_blank"
                      class="btn btn-success btn-sm me-2"
                      title="Contactar por WhatsApp"
                    >
                      <i class="fab fa-whatsapp"></i>
                    </a>
                    <a 
                      v-if="order.customer_email"
                      :href="`mailto:${order.customer_email}`"
                      class="btn btn-info text-white btn-sm"
                      title="Enviar email"
                    >
                      <i class="fas fa-envelope"></i>
                    </a>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fas fa-phone text-primary"></i>
                  <span>{{ order.customer_phone }}</span>
                </div>
                <div class="info-row">
                  <i class="fas fa-box text-primary"></i>
                  <span>{{ order.products?.length || 0 }} productos</span>
                </div>
                <div class="info-row">
                  <i class="fas fa-dollar-sign text-primary"></i>
                  <span class="text-success fw-bold">{{ formatPrice(order.total) }}</span>
                </div>
              </div>

              <!-- Order Actions -->
              <div class="d-flex justify-content-between align-items-end mt-auto gap-2">
                <button class="btn btn-secondary flex-grow-1" @click="viewOrderDetails(order)">
                  <i class="fas fa-eye me-2"></i>
                  Ver Detalles
                </button>
                <button class="btn btn-primary flex-grow-1" @click="editOrder(order)">
                  <i class="fas fa-edit me-2"></i>
                  Editar Estado
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && filteredOrders.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-box-open fa-3x mb-3"></i>
        <h4>No se encontraron pedidos</h4>
        <p>Prueba con otros filtros de búsqueda</p>
      </div>
    </div>
  </section>

  <!-- View Order Modal -->
  <div class="modal fade" id="orderDetailsModal" tabindex="-1" aria-labelledby="orderDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content bg-dark text-light">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title">
            Pedido #{{ selectedOrder?.id }}
            <span 
              class="badge ms-2"
              :class="{
                'bg-warning': selectedOrder?.status === 'pending',
                'bg-success': selectedOrder?.status === 'completed',
                'bg-danger': selectedOrder?.status === 'cancelled'
              }"
            >
              {{ getStatusLabel(selectedOrder?.status) }}
            </span>
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body" v-if="selectedOrder">
          <!-- Customer Information -->
          <div class="section-card mb-4">
            <h6 class="section-title d-flex justify-content-between align-items-center">
              <span><i class="fas fa-user me-2"></i>Información del Cliente</span>
              <div class="contact-buttons">
                <a 
                  v-if="selectedOrder.customer_phone"
                  :href="getWhatsAppLink(selectedOrder.customer_phone)"
                  target="_blank"
                  class="btn btn-success btn-sm me-2"
                  title="Contactar por WhatsApp"
                >
                  <i class="fab fa-whatsapp"></i>
                </a>
                <a 
                  v-if="selectedOrder.customer_email"
                  :href="`mailto:${selectedOrder.customer_email}`"
                  class="btn btn-info text-white btn-sm"
                  title="Enviar email"
                >
                  <i class="fas fa-envelope text-white"></i>
                </a>
              </div>
            </h6>
            <div class="info-grid">
              <!-- Update the username section with fallback -->
              <div class="info-item">
                <span class="label">Usuario:</span>
                <span>{{ selectedOrder.recipient_name || 'Sin nombre' }}</span>
              </div>
              <div class="info-item">
                <span class="label">Recibe:</span>
                <span>{{ selectedOrder.recipient_name }}</span>
              </div>
              <div class="info-item">
                <span class="label">Email:</span>
                <span>{{ selectedOrder.customer_email }}</span>
              </div>
              <div class="info-item">
                <span class="label">Teléfono:</span>
                <span>{{ selectedOrder.customer_phone }}</span>
              </div>
              <div class="info-item">
                <span class="label">Dirección:</span>
                <span>{{ selectedOrder.shipping_address }}</span>
              </div>
            </div>
          </div>

          <!-- Products List -->
          <div class="section-card mb-4">
            <h6 class="section-title">
              <i class="fas fa-box me-2"></i>Productos
            </h6>
            <div class="products-list">
              <div v-for="item in selectedOrder.products" :key="item.id" class="product-item">
                <div class="d-flex gap-3 w-100">
                  <img 
                    :src="item.product_details.image || require('@/assets/zapiola-logo.png')" 
                    :alt="item.product_details.name"
                    class="product-img"
                  >
                  <div class="product-info">
                    <h6>{{ item.product_details.name }}</h6>
                    <p class="text-muted mb-0">{{ item.product_details.brand }}</p>
                    <div class="d-flex justify-content-between align-items-center mt-2">
                      <span>Cantidad: {{ item.quantity }}</span>
                      <span class="text-success">{{ formatPrice(item.price_at_time) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="section-card">
            <h6 class="section-title">
              <i class="fas fa-receipt me-2"></i>Resumen
            </h6>
            <div class="summary-grid">
              <div class="info-item total">
                <span class="label">Total:</span>
                <span class="text-success fw-bold">{{ formatPrice(selectedOrder.total) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer border-top border-secondary">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
          <button type="button" class="btn btn-primary" @click="editOrder(selectedOrder)">
            <i class="fas fa-edit me-2"></i>Editar Estado
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Order Modal -->
  <div class="modal fade" id="editOrderModal" tabindex="-1" aria-labelledby="editOrderModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content bg-dark text-light">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title">Editar Estado del Pedido #{{ selectedOrder?.id }}</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body" v-if="selectedOrder">
          <div class="mb-3">
            <label class="form-label">Estado del Pedido</label>
            <select class="form-select" v-model="selectedOrder.status">
              <option value="pending">Pendiente</option>
              <option value="completed">Completado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
        </div>
        <div class="modal-footer border-top border-secondary">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-primary" @click="updateOrder" :disabled="isUpdating">
            <span v-if="isUpdating">
              <i class="fas fa-spinner fa-spin me-2"></i>Actualizando...
            </span>
            <span v-else>
              <i class="fas fa-save me-2"></i>Guardar Cambios
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { getOrders, updateOrder as updateOrderAPI } from '@/services/orders'
import { useToast } from 'vue-toastification'

const toast = useToast()

// Estado
const orders = ref([])
const isLoading = ref(true)
const error = ref(null)
const searchQuery = ref('')
const selectedOrder = ref(null)
const bootstrap = window.bootstrap
const isUpdating = ref(false)

// Computed Properties
const totalOrders = computed(() => orders.value.length)

const filteredOrders = computed(() => {
  let result = [...orders.value]
  
  // Aplicar búsqueda
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(order => {
      // Use recipient_name as primary search field
      const searchText = [
        order.recipient_name,
        order.customer_email,
        order.customer_phone
      ].filter(Boolean).join(' ').toLowerCase();
      
      return searchText.includes(query);
    })
  }
  
  return result
})

const fetchOrders = async () => {
  try {
    isLoading.value = true
    error.value = null
  
    const response = await getOrders()
    console.log('Orders:', response)
    orders.value = response
  } catch (err) {
    error.value = err.message
    console.error('Error fetching orders:', err)
  } finally {
    isLoading.value = false
  }
}

const debounceSearch = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      // La búsqueda se realiza automáticamente por el computed property
    }, 300)
  }
})()

const editOrder = (order) => {
  selectedOrder.value = { ...order }
  const editOrderModal = new bootstrap.Modal(document.getElementById('editOrderModal'))
  editOrderModal.show()
}

const updateOrder = async () => {
  try {
    isUpdating.value = true;
    
    const updateData = {
      status: selectedOrder.value.status
    };

    await updateOrderAPI(selectedOrder.value.id, updateData);
    
    const modal = bootstrap.Modal.getInstance(document.getElementById('editOrderModal'));
    modal.hide();
    await fetchOrders();
    toast.success(`Orden ${updateData.status === 'cancelled' ? 'cancelada' : 'actualizada'} exitosamente`);
  } catch (error) {
    console.error('Error updating order:', error);
    toast.error('Error al actualizar el estado del pedido');
  } finally {
    isUpdating.value = false;
  }
};

const formatDate = (date) => {
  return new Date(date).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(price);
}

const getStatusIcon = (status) => ({
  pending: 'fas fa-clock',
  completed: 'fas fa-check',
  cancelled: 'fas fa-times'
}[status]);

const getStatusLabel = (status) => ({
  pending: 'Pendiente',
  completed: 'Completado',
  cancelled: 'Cancelado'
}[status]);

const viewOrderDetails = (order) => {
  // Clone the order object to avoid reactivity issues
  selectedOrder.value = { ...order };
  const modal = new bootstrap.Modal(document.getElementById('orderDetailsModal'));
  modal.show();
}

const forceRefresh = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    
    // Use getOrders with ignoreCache parameter
    const response = await getOrders(true);
    orders.value = response;
    toast.success('Pedidos actualizados');
  } catch (err) {
    error.value = err.message;
    console.error('Error refreshing orders:', err);
    toast.error('Error al actualizar los pedidos');
  } finally {
    isLoading.value = false;
  }
};

const getWhatsAppLink = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  const fullPhone = cleanPhone.startsWith('54') ? cleanPhone : `54${cleanPhone}`;
  return `https://wa.me/${fullPhone}`;
};

// Lifecycle
onMounted(() => {
  fetchOrders();
  
  // Actualizar cada 5 minutos
  const refreshInterval = setInterval(() => {
    fetchOrders();
  }, 1 * 60 * 100);

  // Limpiar el intervalo cuando el componente se destruye
  onUnmounted(() => {
    clearInterval(refreshInterval);
  });
})
</script>

<style scoped>
.card {
  border: none;
  border-radius: 12px;
  background: var(--black-color-2);
  transition: transform 0.3s ease;
  height: 100%;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.card-body {
  padding: 1.25rem;
}

.order-id {
  font-family: monospace;
  font-size: 0.9rem;
}

.badge {
  padding: 0.5rem 0.75rem;
  font-weight: 500;
  font-size: 0.85rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.info-row i {
  width: 16px;
  text-align: center;
}

.btn {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  border: none;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
}

.btn-primary:hover {
  filter: brightness(1.1);
}

.skeleton-loader {
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.skeleton-text {
  height: 20px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.section-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
}

.section-title {
  color: var(--white-color);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.info-grid {
  display: grid;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item .label {
  color: var(--white-color-2);
  font-weight: 500;
}

.products-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.product-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.product-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.product-info {
  flex: 1;
}

.form-select {
  background-color: var(--black-color-2);
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.form-select:focus {
  background-color: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: none;
}

.contact-buttons {
  display: flex;
  gap: 0.5rem;
}

.contact-buttons .btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.contact-buttons .btn:hover {
  transform: translateY(-2px);
}

.btn-success {
  background-color: #25D366;
  border-color: #25D366;
}

.btn-info {
  background-color: #0084ff;
  border-color: #0084ff;
}

/* Ajuste para la alineación en las filas de información */
.info-row {
  display: flex;
  align-items: center;
}

.info-row .contact-buttons {
  margin-left: auto;
}
</style>